import { mainNavigationStyles as styles } from "@iewcasia/Overrides/Widgets/Header/MainNavigation";
import { colorStrings, mediaQueriesBP } from "@iewcasia/Utilities/variables";
import baseTheme from "@insite/mobius/globals/baseTheme";
import getColor from "@insite/mobius/utilities/getColor";
import { css } from "styled-components";

export const searchInputWidth = css`
    min-width: 600px;
    ${({ theme }) => {
        const { maxWidths } = theme.breakpoints || baseTheme.breakpoints;
        // we get the max width of the container and subtract the padding
        return css`
            ${mediaQueriesBP.minLG} {
                min-width: calc(${maxWidths[4] - 30}px * 0.5);
            }

            ${mediaQueriesBP.minXLG} {
                min-width: calc(${maxWidths[4] - 30}px * 0.7);
            }
        `;
    }}
`;

styles.container = {
    ...styles.container,
    css: css`
        color: ${getColor("common.background")};
        padding: 0 16px 0 45px;
        display: flex;
        justify-content: left;

        &.container--isc {
            justify-content: flex-start;
            gap: 15px;

            ${mediaQueriesBP.minLG} {
                &.container--compact-header {
                    gap: 7px;
                }

                padding: 0;
                justify-content: space-between;
            }
        }
    `,
};

styles.itemWrapper = {
    ...styles.itemWrapper,
    css: css`
        position: relative;
        overflow: visible;
        display: flex;

        &:hover span + div,
        &:hover button + div {
            display: block;
        }

        &.item-wrapper--search {
            ${searchInputWidth};
            align-items: center;
        }
    `,
};

styles.menuItem = {
    ...styles.menuItem,
    css: css`
        display: flex;
        align-items: center;
        height: 50px;
        padding: 0 10px;

        &.menu-item--active[aria-expanded="true"] {
            svg {
                transform: rotate(-180deg);
            }
        }

        &.quick-order-link {
            background: transparent;
            padding: 0 10px;
        }
    `,
};

styles.menuItemTypography = {
    ...styles.menuItemTypography,
    size: 12,
    color: colorStrings.textMain,
    transform: "uppercase",
    css: css`
        font-weight: 700;
    `,
};

styles.mobileSearchButton = {
    size: 48,
    buttonType: "solid",
    css: css`
        padding: 0 10px;

        color: ${getColor(colorStrings.textMain)};
        background: transparent;
        border: none;
        svg {
            color: ${getColor(colorStrings.textMain)};
        }
    `,
};

styles.mobileWrapper = {
    css: css`
        display: flex;
        justify-content: flex-end;
        padding: 0 20px 0 0;
    `,
};

styles.mobileMenuWrapper = {
    css: css`
        text-align: right;
        margin-right: auto;
    `,
};

// verndale customization
styles.searchInputStyles = {
    input: {
        cssOverrides: {
            formField: css`
                width: 100%;
                color: ${getColor(colorStrings.textMain)};
                border: 1px solid ${getColor(colorStrings.textMain)};
            `,
        },
    },
};

// main navigation styles
styles.megaMenu = {
    ...styles.megaMenu,
    contentBodyProps: {
        css: css`
            ${styles.megaMenu!.contentBodyProps!.css};
            padding: 15px 0 0;
            max-height: max-content;
            width: 100%;
        `,
    },
};

styles.megaMenuGridContainer = {
    ...styles.megaMenuGridContainer,
    css: css`
        padding: 0 15px;

        &.mega-menu-grid-container--content {
            padding: 0;
        }

        &.mega-menu-grid-container--children > ul {
            gap: 10px;
            justify-content: space-between;
        }

        &.mega-menu-grid-container--title {
            ul {
                display: flex;
                justify-content: flex-start;
                align-items: center;
                gap: 100px;
                position: relative;
                padding: 0 0 15px;

                &::after {
                    content: "";
                    position: absolute;
                    left: 50%;
                    bottom: 0;
                    height: 1px;
                    width: 100vw;
                    border-bottom: 1px solid ${getColor(colorStrings.commonBorder)};
                    transform: translateX(-50%);
                }
            }

            li {
                flex: 1 1 auto;
                padding: 0;
                min-width: 100px;
                max-width: max-content;
                font-size: 18px;
            }
        }
    `,
    offsetCss: css`
        &.mega-menu-grid-container--title {
            ul {
                display: flex;
                justify-content: flex-start;
                align-items: center;
                gap: 10px;
            }
        }
    `,
};

styles.megaMenuHeading = {
    ...styles.megaMenuHeading,
    css: css`
        ${styles.megaMenuHeading!.css};
        color: ${getColor(colorStrings.textMain)};
        margin-bottom: 0;
        font-size: 18px;

        &.mega-menu-heading-link {
            color: ${getColor(colorStrings.primaryMain)};
        }

        span {
            font-size: 18px;
        }

        &:hover span {
            font-weight: 700;
            text-decoration: underline;
        }
    `,
};

styles.megaMenuGridItem = {
    ...styles.megaMenuGridItem,
    css: css`
        ${styles.megaMenuGridItem!.css};

        .mega-menu-grid-item--column {
            padding: 15px 0 25px;
            min-width: 225px;
        }

        .mega-menu-link--grandChildContainer {
            gap: 15px;
            display: flex;
            flex-direction: column;
        }

        .mega-menu-link--greatGrandChild {
            max-height: 24px;
        }

        .mega-menu-link--additionalContentContainer {
            gap: 5px;
            display: flex;
            flex-direction: column;
            margin: 10px 0 0;
        }

        .mega-menu-link--grandChild .mega-menu-link {
            font-size: 16px;
            color: ${getColor(colorStrings.textMain)};
            padding: 0;
            margin: 0;
            font-weight: 400;
            text-transform: none;
            position: relative;

            & > span {
                font-weight: 700;
                color: ${getColor(colorStrings.textMain)};
            }

            &:hover span {
                color: ${getColor(colorStrings.textMain)} !important;
            }

            & span > span {
                display: inline;
                white-space: nowrap;

                svg {
                    height: 14px;
                    width: 14px;
                    padding: 2px 0 0;
                    margin: 0;
                }
            }
        }

        .mega-menu-link--greatGrandChild .mega-menu-link {
            & > span {
                font-size: 14px;
                font-weight: 400;
                line-height: normal;
                color: ${getColor(colorStrings.textMain)};
            }

            &:hover span {
                color: ${getColor(colorStrings.textMain)};
            }
        }

        .mega-menu-link--additionalContentTitle {
            font-size: 16px;
            color: ${getColor(colorStrings.textMain)};
            font-weight: 700;

            &:hover {
                color: ${getColor(colorStrings.textMain)};
            }
        }

        .mega-menu-grid-item--additionalContent {
            background: ${getColor(colorStrings.commonGlobalGray04)};
            position: relative;
            padding: 15px 0 0 25px;

            /* we need to expand the bg color to the right edge of the screen since the mega menu is not full width */

            &::after {
                content: "";
                position: absolute;
                left: 100%;
                top: 0;
                height: 100%;
                ${({ theme }) => {
                    const { maxWidths } = theme.breakpoints || baseTheme.breakpoints;

                    // we get the max width of the container to calculate the width of the pseudo element
                    return css`
                        width: calc((100vw - ${maxWidths[4]}px) / 2);
                    `;
                }}
                background: ${getColor(colorStrings.commonGlobalGray04)};
                z-index: -1;
            }
        }

        .mega-menu-link--additionalContentLink span {
            font-size: 14px;
            color: ${getColor(colorStrings.textMain)};
            font-weight: 400;
            text-transform: none;
            padding: 0;
            margin: 0;

            &:hover {
                color: ${getColor(colorStrings.textMain)};
            }
        }
    `,
};
